import * as React from 'react';
import { CheckCircle2, XCircle, Info, X } from 'lucide-react';
import { cn } from '../lib/cn';

/**
 * Minimal dependency-free toast. A module-level event bus lets any code call
 * `toast.success('…')` without threading a context/provider through props; the
 * single `<Toaster/>` mounted at the app root subscribes and renders the stack.
 * Toasts auto-dismiss after `duration` ms (default 4s) and can be dismissed by
 * the close button. Keep it tiny — this is feedback, not a notification centre.
 */
type ToastVariant = 'success' | 'error' | 'info';

export type ToastItem = {
  id: number;
  message: string;
  variant: ToastVariant;
  duration: number;
};

type Listener = (toasts: ToastItem[]) => void;

let toasts: ToastItem[] = [];
let nextId = 1;
const listeners = new Set<Listener>();

function emit() {
  for (const l of listeners) l(toasts);
}

function dismiss(id: number) {
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

function push(message: string, variant: ToastVariant, duration = 4000) {
  const id = nextId++;
  toasts = [...toasts, { id, message, variant, duration }];
  emit();
  if (duration > 0 && typeof window !== 'undefined') {
    window.setTimeout(() => dismiss(id), duration);
  }
  return id;
}

export const toast = {
  success: (message: string, duration?: number) => push(message, 'success', duration),
  error: (message: string, duration?: number) => push(message, 'error', duration),
  info: (message: string, duration?: number) => push(message, 'info', duration),
  dismiss,
};

const ICON: Record<ToastVariant, typeof CheckCircle2> = {
  success: CheckCircle2,
  error: XCircle,
  info: Info,
};

const ACCENT: Record<ToastVariant, string> = {
  success: 'text-success',
  error: 'text-destructive',
  info: 'text-primary',
};

/** Mount once at the app root. Renders the active toast stack (bottom-right). */
export function Toaster() {
  const [items, setItems] = React.useState<ToastItem[]>(toasts);
  React.useEffect(() => {
    listeners.add(setItems);
    return () => {
      listeners.delete(setItems);
    };
  }, []);

  if (items.length === 0) return null;

  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-[100] flex w-full max-w-sm flex-col gap-2">
      {items.map((t) => {
        const Icon = ICON[t.variant];
        return (
          <div
            key={t.id}
            role="status"
            className="pointer-events-auto flex items-start gap-2 rounded-md border bg-background p-3 text-sm shadow-lg"
          >
            <Icon className={cn('mt-0.5 h-4 w-4 flex-none', ACCENT[t.variant])} />
            <span className="flex-1 leading-snug text-foreground">{t.message}</span>
            <button
              type="button"
              aria-label="Dismiss"
              onClick={() => dismiss(t.id)}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
